import { type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";

import {
  AppCard,
  AppCardBody,
  AppHeading,
  AppPage,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type Highlight = {
  title: string;
  description: string;
};

interface AuthScaffoldProps {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
  sideTitle?: string;
  sideDescription?: string;
  highlights?: Highlight[];
  showContext?: boolean;
}

interface AuthInfoPanelProps {
  children: ReactNode;
}

interface AuthDividerProps {
  label?: string;
}

interface AuthStepDotsProps {
  current: number;
  total: number;
}

interface AuthChoiceCardProps {
  selected?: boolean;
  children: ReactNode;
  onPress: () => void;
}

interface AuthFooterTextProps {
  children: ReactNode;
}

const defaultHighlights: Highlight[] = [
  {
    title: "Real-world adventures",
    description: "Days out, festivals, camping, and local plans that feel like actual life.",
  },
  {
    title: "A proper community",
    description: "Message threads, rooms, and ongoing conversations that feel like a village, not a noticeboard.",
  },
  {
    title: "Practical support",
    description: "Benefits, rights, housing, and help that is useful when things are messy.",
  },
];

const AuthScaffold = ({
  eyebrow,
  title,
  description,
  children,
  footer,
  sideTitle = "Single-parent families deserve something warmer than another app.",
  sideDescription = "Kindred is built to feel grounded, welcoming, and genuinely useful, whether you need company, advice, or a reason to get out of the house.",
  highlights = defaultHighlights,
  showContext = true,
}: AuthScaffoldProps) => {
  return (
    <YStack minHeight="100vh" backgroundColor="$canvas" position="relative" overflow="hidden">
      <YStack pointerEvents="none" position="absolute" inset={0}>
        <YStack
          position="absolute"
          top={-72}
          left={-48}
          width={240}
          height={240}
          borderRadius="$pill"
          backgroundColor="$accentSoft"
          opacity={0.44}
        />
        <YStack
          position="absolute"
          top={-24}
          right={-72}
          width={280}
          height={280}
          borderRadius="$pill"
          backgroundColor="$primarySoft"
          opacity={0.34}
        />
        <YStack
          position="absolute"
          bottom={120}
          right={-28}
          width={160}
          height={160}
          borderRadius="$pill"
          backgroundColor="$secondarySoft"
          opacity={0.26}
        />
      </YStack>

      <AppPage minHeight="100vh" justifyContent="center" gap="$md" maxWidth={960}>
        <XStack justifyContent="center">
          <Link to="/" style={{ textDecoration: "none" }}>
            <YStack
              alignItems="center"
              justifyContent="center"
              backgroundColor="$cardBackground"
              borderWidth={1}
              borderColor="$borderColor"
              borderRadius="$md"
              paddingHorizontal="$lg"
              paddingVertical="$md"
              shadowColor="$shadowColor"
              shadowOffset={{ width: 0, height: 10 }}
              shadowOpacity={0.08}
              shadowRadius={22}
            >
              <img
                src="/kindred-logo-full.png"
                alt="Kindred Community"
                style={{ display: "block", height: 44, width: "auto" }}
              />
            </YStack>
          </Link>
        </XStack>

        <AppCard backgroundColor="$panelBackground" borderRadius="$xl">
          <YStack height={8} backgroundColor="$secondary" />
          <AppCardBody position="relative" px="$xl" py="$xl" gap="$md">
            <YStack
              pointerEvents="none"
              position="absolute"
              top={40}
              left={-34}
              width={120}
              height={120}
              borderRadius="$pill"
              backgroundColor="$secondarySoft"
              opacity={0.22}
            />
            <YStack
              pointerEvents="none"
              position="absolute"
              top={-18}
              right={-28}
              width={168}
              height={168}
              borderRadius="$pill"
              backgroundColor="$accentSoft"
              opacity={0.28}
            />

            <YStack gap="$sm">
              <AppText
                color="$primary"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$sm"
                lineHeight="$sm"
                textTransform="uppercase"
                letterSpacing={1.2}
              >
                {eyebrow}
              </AppText>
              <AppHeading
                fontSize="$hero"
                lineHeight="$hero"
                letterSpacing="$hero"
                $gtSm={{ fontSize: "$heroLg", lineHeight: "$heroLg", letterSpacing: "$heroLg" }}
              >
                {title}
              </AppHeading>
              <AppText color="$colorMuted" maxWidth={560}>
                {description}
              </AppText>
            </YStack>

            <YStack gap="$sm">{children}</YStack>

            {footer ? (
              <YStack borderTopWidth={1} borderColor="$borderColor" paddingTop="$md">
                {footer}
              </YStack>
            ) : null}
          </AppCardBody>
        </AppCard>

        {showContext && highlights.length > 0 ? (
          <XStack flexWrap="wrap" gap="$sm">
            {highlights.map((highlight) => (
              <AppCard
                key={highlight.title}
                flexBasis={0}
                flexGrow={1}
                minWidth={220}
                backgroundColor="$cardBackground"
              >
                <AppCardBody>
                  <AppText fontFamily={kindredFontFamilies.sansBold} fontSize="$sm" lineHeight="$sm">
                    {highlight.title}
                  </AppText>
                  <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                    {highlight.description}
                  </AppText>
                </AppCardBody>
              </AppCard>
            ))}
          </XStack>
        ) : null}

        {showContext ? (
          <AppCard backgroundColor="$cardBackground">
            <AppCardBody>
              <AppText
                color="$primary"
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$sm"
                lineHeight="$sm"
                textTransform="uppercase"
                letterSpacing={1.2}
              >
                Why Kindred
              </AppText>
              <AppHeading fontSize="$xxl" lineHeight="$xxl" letterSpacing="$xxl">
                {sideTitle}
              </AppHeading>
              <AppText color="$colorMuted">{sideDescription}</AppText>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                Kindred is for single mums, single dads, and solo parent families who want calmer community and practical support.
              </AppText>
            </AppCardBody>
          </AppCard>
        ) : null}
      </AppPage>
    </YStack>
  );
};

export const AuthInfoPanel = ({ children }: AuthInfoPanelProps) => (
  <AppCard backgroundColor="$background" borderRadius="$md">
    <AppCardBody py="$sm" gap="$xs">
      {children}
    </AppCardBody>
  </AppCard>
);

export const AuthDivider = ({ label = "or continue with" }: AuthDividerProps) => (
  <XStack alignItems="center" gap="$sm">
    <YStack flex={1} height={1} backgroundColor="$borderColor" />
    <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
      {label}
    </AppText>
    <YStack flex={1} height={1} backgroundColor="$borderColor" />
  </XStack>
);

export const AuthStepDots = ({ current, total }: AuthStepDotsProps) => (
  <XStack justifyContent="center" gap={8}>
    {Array.from({ length: total }, (_, index) => {
      const step = index + 1;
      return (
        <YStack
          key={step}
          width={10}
          height={10}
          borderRadius="$pill"
          backgroundColor={step === current ? "$primary" : "$borderColor"}
        />
      );
    })}
  </XStack>
);

export const AuthChoiceCard = ({ selected = false, children, onPress }: AuthChoiceCardProps) => (
  <TamaguiButton
    unstyled
    width="100%"
    alignItems="stretch"
    justifyContent="center"
    borderWidth={1}
    borderRadius="$lg"
    borderColor={selected ? "$primary" : "$borderColor"}
    backgroundColor={selected ? "$primarySoft" : "$background"}
    paddingHorizontal="$md"
    paddingVertical="$md"
    pressStyle={{ opacity: 0.92 }}
    hoverStyle={{ borderColor: "$primary" }}
    onPress={onPress}
  >
    {children}
  </TamaguiButton>
);

export const AuthFooterText = ({ children }: AuthFooterTextProps) => (
  <AppText textAlign="center" color="$colorMuted" fontSize="$sm" lineHeight="$sm">
    {children}
  </AppText>
);

export default AuthScaffold;
